import { useState } from 'react';
import type { AlbumDetail, PhotoCard, Visibility } from '@photos/shared';
import { useAlbumAction, useUpdatePhotos, type AlbumAction } from '../../api/hooks';
import { AlbumPickerDialog } from '../../components/AlbumPicker';
import { TagsDialog } from '../../components/dialogs/TagsDialog';
import { Icon } from '../../components/Icon';
import { visibilityToast } from '../../lib/visibility';
import { useSession } from '../../state/session';
import { useToast } from '../../state/toasts';
import { ReleaseDialog, useUndoToast } from './studioUi';

type Dialog = 'tags' | 'release' | 'move' | 'add' | null;

/** The albums a photo is known to be in from the grid: its home album and the album being viewed. */
export const knownAlbums = (p: Pick<PhotoCard, 'homeAlbumId'>, album?: { id: string }) =>
  [p.homeAlbumId, album?.id].filter((x): x is string => !!x);

const n = (count: number) => `${count.toLocaleString('en-US')} ${count === 1 ? 'photo' : 'photos'}`;

/**
 * Studio selection actions (inside the selection bar): Release / Unrelease, Tags…, ★ Pick, Mark restricted /
 * Unrestrict, Move to album…, Add to album…, Remove from album (references only), Trash. Each shows only when the
 * caller may do it for the whole selection.
 */
export function StudioActions({ photos, album, onDone }: { photos: PhotoCard[]; album?: AlbumDetail; onDone(): void }) {
  const { access } = useSession();
  const update = useUpdatePhotos();
  const albumAction = useAlbumAction();
  const undoToast = useUndoToast();
  const toast = useToast();
  const [dialog, setDialog] = useState<Dialog>(null);
  const busy = update.isPending || albumAction.isPending;
  const ids = photos.map((p) => p.id);
  const of = (v: Visibility) => photos.filter((p) => p.visibility === v);
  const unreleased = of('unreleased');
  const released = of('released');
  const restricted = of('restricted');
  const canTag = photos.every((p) => access.canTagIn(knownAlbums(p, album)));
  const references = album ? photos.filter((p) => p.homeAlbumId !== album.id) : [];
  const fail = (e: Error) => toast({ icon: 'info', title: 'That didn’t work', sub: e.message });

  const setVisibility = (list: PhotoCard[], to: Visibility) =>
    update.mutate(
      { ids: list.map((p) => p.id), albumId: album?.id, visibility: to },
      {
        onSuccess: (r) => {
          undoToast(visibilityToast(to, list.length), r.activityIds[0]);
          onDone();
        },
        onError: fail,
      },
    );

  const pick = () => {
    const all = photos.every((p) => p.picked);
    update.mutate(
      { ids, albumId: album?.id, picked: !all },
      {
        onSuccess: (r) => {
          undoToast({ icon: 'star', title: all ? `Removed ${n(photos.length)} from Picks` : `Added ${n(photos.length)} to Picks` }, r.activityIds[0]);
          onDone();
        },
        onError: fail,
      },
    );
  };

  const trash = () =>
    update.mutate(
      { ids, albumId: album?.id, trash: true },
      {
        onSuccess: (r) => {
          undoToast({ icon: 'trash', title: `Moved ${n(photos.length)} to Trash`, sub: 'Undo from Activity if that was a mistake.' }, r.activityIds[0]);
          onDone();
        },
        onError: fail,
      },
    );

  const albumChange = (action: AlbumAction, target: { id: string; name: string }, list: string[], applyTagChanges = false) =>
    albumAction.mutate(
      { action, albumId: target.id, ids: list, applyTagChanges },
      {
        onSuccess: (r) => {
          setDialog(null);
          const title =
            action === 'move'
              ? `Moved ${n(r.changed)} to ${target.name}`
              : action === 'add'
                ? `Added ${n(r.changed)} to ${target.name}`
                : action === 'copy'
                  ? `Copied ${n(r.changed)} to ${target.name}`
                  : `Removed ${n(r.changed)} from ${target.name}`;
          const sub =
            action === 'add' ? 'The same photos now show in both albums.' : action === 'copy' ? 'Separate copies: changes to one don’t affect the other.' : undefined;
          undoToast({ icon: 'folder', title, sub }, r.activityId);
          onDone();
        },
        onError: fail,
      },
    );

  return (
    <>
      {access.canRelease && unreleased.length > 0 && (
        <button type="button" className="btn primary" onClick={() => setDialog('release')} disabled={busy}>
          <Icon name="globe" />
          Release{unreleased.length < photos.length ? ` ${unreleased.length}` : ''}
        </button>
      )}
      {access.canRelease && released.length > 0 && (
        <button type="button" className="btn" onClick={() => setVisibility(released, 'unreleased')} disabled={busy}>
          <Icon name="eyeOff" />
          Unrelease{released.length < photos.length ? ` ${released.length}` : ''}
        </button>
      )}
      {(canTag || access.canRestrict || access.isMedia) && <span className="sep" />}
      {canTag && (
        <button type="button" className="btn" onClick={() => setDialog('tags')}>
          <Icon name="tag" />
          Tags…
        </button>
      )}
      {canTag && (
        <button type="button" className="btn" onClick={pick} disabled={busy}>
          <Icon name="star" />
          Pick
        </button>
      )}
      {access.canRestrict && restricted.length < photos.length && (
        <button type="button" className="btn" onClick={() => setVisibility([...unreleased, ...released], 'restricted')} disabled={busy}>
          <Icon name="lock" />
          Mark restricted
        </button>
      )}
      {access.canRestrict && restricted.length > 0 && (
        <button type="button" className="btn" onClick={() => setVisibility(restricted, 'unreleased')} disabled={busy} title="Back to unreleased">
          <Icon name="lock" />
          Unrestrict
        </button>
      )}
      {access.isMedia && (
        <>
          <button type="button" className="btn" onClick={() => setDialog('move')}>
            <Icon name="move" />
            Move to album…
          </button>
          <button type="button" className="btn" onClick={() => setDialog('add')}>
            <Icon name="folder" />
            Add to album…
          </button>
          {album && references.length > 0 && (
            <button type="button" className="btn" onClick={() => albumChange('remove', album, references.map((p) => p.id))} disabled={busy}>
              <Icon name="x" />
              Remove from album{references.length < photos.length ? ` ${references.length}` : ''}
            </button>
          )}
          <button type="button" className="btn danger" aria-label="Move to Trash" title="Move to Trash" onClick={trash} disabled={busy}>
            <Icon name="trash" />
          </button>
        </>
      )}
      {dialog === 'tags' && <TagsDialog photos={photos} albumId={album?.id} onClose={() => setDialog(null)} onDone={onDone} />}
      {dialog === 'release' && (
        <ReleaseDialog target={{ ids: unreleased.map((p) => p.id), albumId: album?.id }} onClose={() => setDialog(null)} onDone={onDone} />
      )}
      {(dialog === 'move' || dialog === 'add') && (
        <AlbumPickerDialog
          title={dialog === 'move' ? `Move ${n(photos.length)} to another album` : `Add ${n(photos.length)} to an album`}
          help={
            dialog === 'move'
              ? 'Changes their home album. They leave the album they live in now.'
              : 'Choose an album, then whether the photos show in both albums or get separate copies.'
          }
          confirmLabel={dialog === 'move' ? 'Move' : 'Add'}
          excludeIds={album ? [album.id] : []}
          allowCreate={access.isMedia}
          busy={albumAction.isPending}
          tags={{ mode: dialog, photos }}
          copyChoice={dialog === 'add'}
          onPick={(target, apply, asCopy) => albumChange(asCopy ? 'copy' : dialog, target, ids, apply)}
          onClose={() => setDialog(null)}
        />
      )}
    </>
  );
}
